<template>
  <div>
    <SelectedFilters @change="selectedFiltersChange" />

    <div v-for="(section, index) in orderOfFilterSections" :key="section">
      <template v-if="section === 'price' && isMobile">
        <h3 class="font-semibold leading-6 mb-2 text-init mt-0">
          Price
        </h3>
        <Price
          price-method-display-style="buttonGroup"
          :form="form"
          @change="changePrice"
          @priceChange="finishChangingPrice"
        />
        <hr class="more--options__hr">
      </template>

      <template v-if="section === 'cateringAndDrinks'">
        <MoreSection
          title="Catering and Drinks"
          :values="form.catering"
          :options="cateringOptions"
          data-test-id="more__filters-catering-and-drinks-section"
          @change="change('catering', $event)"
        />

        <hr class="more--options__hr">

        <MoreSection
          title="Menu offer"
          :values="form.catering"
          :options="menuOptions"
          data-test-id="more__filters-menu"
          @change="change('catering', $event)"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'venueType' && venueTypes.length">
        <MoreSection
          ref="venueTypesSection"
          title="Venue type"
          :values="chosenVenueTypes"
          :options="venueTypes"
          data-test-id="more__filters-venueTypes-section"
          @change="setVenueType($event.option.id)"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'style'">
        <StyleChips @change="$emit('change', $event)" />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'facilities'">
        <FacilitiesChips
          :selected-room-features="form.roomFeatures"
          :room-features="roomFeatures"
          @change="updateRoomFeatures"
        />

        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'allowedEvents'">
        <MoreSection
          title="Allowed Events"
          :values="formAllowedEvents"
          :options="options.allowedEvents"
          data-test-id="more__filters-allowed-events-section"
          @change="change('allowedEvents', $event)"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'areaType'">
        <h3 class="font-semibold leading-6 mb-4 text-base mt-0" ref="areaTypeSection">
          Area type
        </h3>
        <BoxFilterSection
          v-for="part in visibleVenueParts"
          :id="'area-type_' + 1"
          class="mb-4"
          :key="part.id"
          :title="part.filter_label || part.label"
          :content="part.description"
          :model-value="$store.state.filters.venueParts.value.includes(part.id)"
          @update:modelValue="setVenuePart(part.id, $event)"
        >
          <template #icon>
            <VenuePartIcon :value="part.id" class="mr-2 w-6 h-6 shrink-0" />
          </template>
        </BoxFilterSection>

        <a
          v-show="visibleVenueParts.length <= 3"
          class="font-semibold cursor-pointer"
          @click="showingAllAreas = true"
        >
          + Show all
          <span v-if="hiddenSelectedVenueParts">({{ hiddenSelectedVenueParts }})</span>
        </a>
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'supervenues'">
        <h3 class="font-semibold leading-6 mb-4 text-base mt-0">
          Top-tier venues
        </h3>
        <BoxFilterSection
          id="supervenues"
          title="Only show Supervenues"
          content="Supervenues are experienced, highly rated venues that are committed to providing great service for all customers."
          :model-value="form.superVenuesOnly"
          @update:modelValue="$emit('change', { value: $event, field: 'superVenuesOnly' })"
        >
          <template #icon>
            <img
              :src="'/build/images/supervenue.svg'"
              class="supervenue-badge-xs mr-2"
              width="24"
              height="24"
            >
          </template>
        </BoxFilterSection>
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'cancellation'">
        <SwitchFilterSection
          title="Cancellation flexibility"
          content="Only show venues that offer cancellation flexibility"
          :model-value="form.flexibleCancellation"
          @update:modelValue="$emit('change', { value: $event, field: 'flexibleCancellation' })"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'ageRestriction'">
        <SwitchFilterSection
          title="Age restriction"
          content="Only show venues without age restriction"
          :model-value="form.noAgeRestriction"
          @update:modelValue="$emit('change', { value: $event, field: 'noAgeRestriction' })"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'parkingAndAccommodation'">
        <MoreSection
          title="Parking and Accommodation"
          :values="form.venueFeatures"
          :options="options.venueFeatures"
          data-test-id="more__filters-parking-and-accomodation-section"
          @change="change('venueFeatures', $event)"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'accessibilityFeatures'">
        <MoreSection
          ref="accessibilityFeaturesSection"
          title="Accessibility features"
          :values="$store.state.filters.accessibilityFeatures.value"
          :options="options.accessibilityFeatures"
          data-test-id="more__filters-accessibility-features-section"
          @change="setAccessibilityFeature($event)"
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>

      <template v-else-if="section === 'layout'">
        <h3 class="font-semibold leading-6 mb-4 text-base mt-0">
          Layout
        </h3>
        <LayoutFilter
          :room-layout="$store.state.filters.layout.value"
          @change="setRoomLayout($event)"
          @click.stop
        />
        <hr v-if="index !== orderOfFilterSections.length - 1" class="more--options__hr">
      </template>
    </div>
  </div>
</template>

<script>
import PlusIcon from '@tagvenue/icons/24/bold/general/plus.svg';

import VenuePartIcon from '@/components/VenuePartIcon.vue';
import BoxFilterSection from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/More/BoxFilterSection.vue';
import LayoutFilter from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/More/LayoutFilter.vue';
import SelectedFilters from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/More/SelectedFilters.vue';
import SwitchFilterSection from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/More/SwitchFilterSection.vue';
import Price from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/Price/Price.vue';
import options from '@/modules/public/search/store/options.json';

import FacilitiesChips from './FacilitiesChips.vue';
import MoreSection from './MoreSection.vue';
import StyleChips from './StyleChips.vue';

export default {
  name: 'MoreFilters',
  emits: ['change'],
  components: { FacilitiesChips, LayoutFilter, SelectedFilters, Price, VenuePartIcon, BoxFilterSection, SwitchFilterSection, MoreSection, StyleChips },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
      validator: (form) => {
        const { catering, roomFeatures, allowedEvents, venueFeatures, flexibleCancellation, superVenuesOnly } = form;
        const cateringValid = catering && typeof catering === 'object';
        const roomFeaturesValid = roomFeatures && typeof roomFeatures === 'object';
        const allowedEventsValid = allowedEvents && typeof allowedEvents === 'object';
        const venueFeaturesValid = venueFeatures && typeof venueFeatures === 'object';

        const flexibleCancellationValid = typeof flexibleCancellation === 'boolean';
        const superVenuesOnlyValid = typeof superVenuesOnly === 'boolean';
        return cateringValid && roomFeaturesValid && allowedEventsValid && flexibleCancellationValid && venueFeaturesValid && superVenuesOnlyValid;
      },
    },
  },
  data() {
    return {
      PlusIcon,
      showingAllAreas: false,
      options,
    };
  },
  computed: {
    orderOfFilterSections() {
      // https://coda.io/d/Product-Space_dsygeEPmV7S/Filters-modal-update_suF_EPqc?navSelection=Folders#_luB2kx-K
      if (this.$store.state.eventCategory === 'meeting') {
        return ['price', 'layout', 'cateringAndDrinks', 'areaType', 'venueType', 'style', 'facilities', 'accessibilityFeatures', 'supervenues', 'cancellation'];
      }
      if (this.$store.state.eventCategory === 'business') {
        return ['price', 'layout', 'cateringAndDrinks', 'areaType', 'venueType', 'style', 'facilities', 'allowedEvents', 'accessibilityFeatures', 'supervenues', 'cancellation'];
      }
      if (this.$store.state.eventCategory === 'party') {
        return ['price', 'cateringAndDrinks', 'areaType', 'layout', 'venueType', 'style', 'facilities', 'allowedEvents', 'accessibilityFeatures', 'ageRestriction', 'supervenues', 'cancellation'];
      }
      if (this.$store.state.eventCategory === 'wedding') {
        return ['price', 'cateringAndDrinks', 'layout', 'areaType', 'venueType', 'style', 'facilities', 'accessibilityFeatures', 'supervenues', 'ageRestriction', 'allowedEvents', 'cancellation'];
      }
      if (this.$store.state.eventCategory === 'production') {
        return ['price', 'areaType', 'venueType', 'style', 'facilities', 'accessibilityFeatures', 'supervenues'];
      }

      // venue_type
      return ['price', 'cateringAndDrinks', 'layout', 'areaType', 'venueType', 'style', 'facilities', 'allowedEvents', 'accessibilityFeatures', 'ageRestriction', 'supervenues', 'cancellation'];
    },
    venuePartsList() {
      return this.$store.state.venuePartsList.map(part => ({
        id: part.value,
        label: part.label,
        filter_label: part.filter_label,
        description: part.description,
      }));
    },
    visibleVenueParts() {
      return this.showingAllAreas
        ? this.venuePartsList
        : this.venuePartsList.slice(0, 3);
    },
    hiddenSelectedVenueParts() {
      return this.venuePartsList.slice(3).reduce((acc, part) => acc + (this.$store.state.filters.venueParts.value.includes(part.id) ? 1 : 0), 0);
    },
    venueTypes() {
      if (this.$store.state.venueTypesPerEventCategoryConfig[this.$store.state.eventCategory]) {
        return this.$store.state.venueTypesPerEventCategoryConfig[this.$store.state.eventCategory];
      }
      return [];
    },
    roomFeatures() {
      return [...options.roomFeatures];
    },
    cateringOptions() {
      return [...options.cateringOptions]
        .filter(option => option.type !== 'menu');
    },

    menuOptions() {
      return [...options.cateringOptions]
        .filter(option => option.type === 'menu');
    },
    chosenVenueTypes() {
      return this.$store.state.filters.venueTypes.value.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
    },

    formAllowedEvents() {
      return {
        ...this.form.allowedEvents,
        ...this.form.roomFeatures,
        [options.weddingLicenseId]: this.form.roomFeatures[options.weddingLicenseId],
        [options.temporaryEventNoticesId]: this.form.roomFeatures[options.temporaryEventNoticesId],
      };
    },
  },
  methods: {
    selectedFiltersChange({ field, key, value }) {
      if (field === 'venueParts') {
        this.setVenuePart(key, value);
        return;
      }
      if (field === 'venueTypes') {
        this.setVenueType(key);
        return;
      }

      this.$emit('change', {
        field,
        key,
        value,
      });
    },
    change(field, { value, option = {} }) {
      this.$emit('change', {
        value,
        field: option.param || field,
        key: option.id,
      });
    },
    setAccessibilityFeature({ option, value }) {
      this.$emit('change', {
        field: 'accessibilityFeatures',
        key: option.id,
        value,
      });
    },
    setVenuePart(partId, value) {
      let venueParts = [...this.$store.state.filters.venueParts.value];
      if (value && !venueParts.includes(partId)) {
        venueParts.push(partId);
      } else {
        venueParts = venueParts.filter(id => id !== partId);
      }
      this.$emit('change', {
        field: 'venueParts',
        value: venueParts,
      });
    },
    setVenueType(value) {
      let venueTypes = [...this.$store.state.filters.venueTypes.value];
      if (!venueTypes.includes(value)) {
        venueTypes.push(value);
      } else {
        venueTypes = venueTypes.filter(id => id !== value);
      }
      this.$emit('change', {
        field: 'venueTypes',
        value: venueTypes,
      });
    },
    finishChangingPrice() {
      this.$emit('change', {
        field: 'price',
        value: this.form.priceRange,
      });
    },
    changePrice(key, value) {
      if (key === 'priceMethod') {
        this.$emit('change', {
          field: 'priceMethod',
          value: value === this.form.priceMethod ? null : value,
        });
      }

      if (key === 'priceRange') {
        this.$emit('change', {
          field: 'priceRange',
          value,
        });
      }
    },
    updateRoomFeatures(key, value) {
      this.$emit('change', {
        field: 'roomFeatures',
        key,
        value,
      });
    },
    setRoomLayout(value) {
      this.$emit('change', {
        field: 'roomLayout',
        value,
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$store.state.triggerSection === 'venueTypes') {
        this.$refs.venueTypesSection.$data.showMore = true;
        this.$refs.venueTypesSection.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (this.$store.state.triggerSection === 'areaTypes') {
        this.showingAllAreas = true;
        this.$refs.areaTypeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (this.$store.state.triggerSection === 'accessibilityFeatures') {
        this.$refs.accessibilityFeaturesSection.$data.showMore = true;
        this.$refs.accessibilityFeaturesSection.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      this.$store.commit('setTriggerSection', null);
    });
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/base/variables";
@use "@sass/design-system/variables" as ds-variables;

@media only screen and (max-width: variables.$screen-xs-max) {
  .more-options__toggle__label {
    flex-basis: 65%;
  }
}

.more--options__alcohol-select {
  width: 120px;
  margin-top: 0.5rem;
  margin-left: 2.25rem;
}

.more-options__supervenue-label {
  display: inline-block;
  color: ds-variables.$green;
  border: 1px solid ds-variables.$green;
  border-radius: 4px;
  box-sizing: border-box;
  padding: 4px;
  font-size: 11px;
  font-weight: 600;
}

.more--options__hr {
  border-top: 1px solid ds-variables.$grey-300;
  margin: 24px 0;
}
</style>
